import moment from 'moment';
import { Link } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import {
    ArrowImport20Regular,
    CheckmarkCircle20Regular,
    Delete20Regular,
    DismissCircle20Regular,
    DocumentBulletList20Regular,
    Edit20Regular,
    Eye20Regular,
    MoneyHand20Regular,
    Search20Regular
} from '@fluentui/react-icons';

import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Payment } from '../../../models/Entities/CashFlow/Payment/Payment';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { ConfirmationModalComponent } from '../../../commons/component/ConfirmationModal/ConformationModal.Component';
import { StatusPill } from '../../../theme/components/StatusPill';
import { agingBucket, currencyOf, daysSince, initialsOf, isPending } from './Cashflow.Dashboard.Functions';

const fmt = (amount: number | undefined, currency: string) => formatCurrencyAmountWithIssue(amount ?? 0, currency);
const fmtDate = (value: Date | string | undefined | null) => (value ? moment(value).format('DD/MM/YYYY') : '—');

/** Totales por moneda de una columna, para el pie de la tabla. */
const totalsByCurrency = <T,>(items: T[], amountOf: (item: T) => number, currencyOfItem: (item: T) => string): string => {
    const sums = new Map<string, number>();
    items.forEach((item) => {
        const currency = currencyOfItem(item);
        sums.set(currency, (sums.get(currency) ?? 0) + amountOf(item));
    });
    return Array.from(sums.entries()).map(([currency, amount]) => fmt(amount, currency)).join(' · ') || '—';
};

const PartyCell = ({ name, comment }: { name: string; comment?: string }) => (
    <span className="cf-party-cell" title={comment ? `${name} · ${comment}` : name}>
        <span className="cf-avatar" aria-hidden>{initialsOf(name)}</span>
        <span>
            <span className="cf-party-cell__name">{name}</span>
            {comment && <span className="cf-party-cell__comment">{comment}</span>}
        </span>
    </span>
);

const AgingTag = ({ days }: { days: number }) => {
    const bucket = agingBucket(days);
    return <span className={`cf-tag cf-tag--${bucket}`}>{days} días</span>;
};

const EmptyState = ({ title, hint }: { title: string; hint: string }) => (
    <div className="cf-empty">
        <MoneyHand20Regular />
        <span className="cf-empty__title">{title}</span>
        <span>{hint}</span>
    </div>
);

// ── Deudas ────────────────────────────────────────────────────────────────

export interface DebtsTableProps {
    /** Las filas de la página actual. */
    debts: Debt[];
    /** Todas las filas del filtro, para que el pie sume el total y no la página. */
    allDebts?: Debt[];
    /** 'receivable': el centro cobra; 'payable': el centro paga. */
    kind: 'receivable' | 'payable';
    /** Sólo lo saldado (historial), con la verificación de estado. Mezcla los dos sentidos. */
    settledView?: boolean;
    /** Quién es "nosotros": decide el sentido de cada deuda en la vista de saldadas. */
    entityIdentifier?: string;
    isLoading: boolean;
    nameOf: (identifier: string) => string;
    onView: (debt: Debt) => void;
    onEdit: (debt: Debt) => void;
    onDelete: (debt: Debt) => void;
    /** Registrar el cobro (o pago) de esta deuda, con todo precargado. */
    onSettle?: (debt: Debt) => void;
    isDeleting: boolean;
    onCheckStatus: (debt: Debt) => void;
    isCheckingStatus: boolean;
    checkedStatusId: number | undefined;
    checkStatusResult: boolean | undefined;
    checkStatusDone: boolean | undefined;
}

export const DebtsTable = (props: DebtsTableProps) => {
    // En las saldadas conviven cobros y pagos: el sentido se decide por deuda.
    const directionOf = (debt: Debt): 'receivable' | 'payable' =>
        props.settledView && props.entityIdentifier ? (debt.creditor === props.entityIdentifier ? 'receivable' : 'payable') : props.kind;
    const counterpartOf = (debt: Debt) => (directionOf(debt) === 'receivable' ? debt.debtor : debt.creditor);
    const paidLabel = props.settledView ? 'Pagado' : (props.kind === 'receivable' ? 'Cobrado' : 'Pagado');
    const allDebts = props.allDebts ?? props.debts;

    if (props.isLoading && props.debts.length === 0) {
        return <div className="cf-loading"><Spinner size="extra-tiny" /> Cargando deudas…</div>;
    }

    if (props.debts.length === 0) {
        return props.settledView
            ? <EmptyState title="Todavía no hay deudas saldadas" hint="Cuando una deuda se cobre por completo va a aparecer acá." />
            : <EmptyState
                title={props.kind === 'receivable' ? 'No hay nada pendiente de cobro' : 'No hay nada pendiente de pago'}
                hint="Podés registrar una deuda con el botón Agregar." />;
    }

    const renderVerify = (debt: Debt) => {
        if (props.isCheckingStatus && props.checkedStatusId === debt.id) return <Spinner size="extra-tiny" />;
        if (!props.isCheckingStatus && props.checkStatusDone && props.checkedStatusId === debt.id) {
            return props.checkStatusResult
                ? <span className="cf-tag cf-tag--ok"><CheckmarkCircle20Regular /> Saldada</span>
                : <span className="cf-tag cf-tag--bad"><DismissCircle20Regular /> Con saldo</span>;
        }
        return (
            <button type="button" className="cf-icon-btn" title="Verificar estado en el sistema" aria-label="Verificar estado" onClick={() => props.onCheckStatus(debt)}>
                <Search20Regular />
            </button>
        );
    };

    return (
        <div className="cf-table-wrap">
            <table className="cf-table">
                <thead>
                    <tr>
                        <th>Contraparte</th>
                        <th>Liquidación</th>
                        <th>Fecha</th>
                        <th>{props.settledView ? 'Estado' : 'Antigüedad'}</th>
                        <th className="cf-r">Monto</th>
                        <th className="cf-r">{paidLabel}</th>
                        <th className="cf-r">Pendiente</th>
                        <th className="cf-r">{props.settledView ? 'Verificar' : ''}</th>
                    </tr>
                </thead>
                <tbody>
                    {props.debts.map((debt) => {
                        const currency = currencyOf(debt);
                        const pending = isPending(debt);
                        const days = daysSince(debt.debtDate);
                        const attention = pending && days > 90;
                        const name = props.nameOf(counterpartOf(debt));
                        return (
                            <tr key={debt.id} className={attention ? 'is-attention' : undefined}>
                                <td><PartyCell name={name} comment={debt.comments || undefined} /></td>
                                <td>
                                    {debt.settlementId
                                        ? <Link className="cf-link" to={`/settlements/${debt.settlementId}`} title="Ir a la liquidación">LIQ-{debt.settlementId}</Link>
                                        : <span className="cf-muted">—</span>}
                                </td>
                                <td className="cf-num cf-muted">{fmtDate(debt.debtDate)}</td>
                                <td>
                                    {pending
                                        ? <AgingTag days={days} />
                                        : (
                                            <span className="cf-tags">
                                                <span className="cf-tag cf-tag--settled">Saldada</span>
                                                {props.settledView && (
                                                    <span className={`cf-tag cf-tag--${directionOf(debt) === 'receivable' ? 'in' : 'out'}`}>
                                                        {directionOf(debt) === 'receivable' ? 'Cobro' : 'Pago'}
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                </td>
                                <td className="cf-r cf-num cf-amount">{fmt(debt.originalAmount, currency)}</td>
                                <td className="cf-r cf-num cf-amount cf-amount--muted">{fmt(debt.paidAmount, currency)}</td>
                                <td className={`cf-r cf-num cf-amount${pending ? ' cf-amount--pending' : ' cf-amount--muted'}`}>
                                    {pending ? fmt(debt.pendingAmount, currency) : '—'}
                                </td>
                                <td className="cf-r">
                                    <span className="cf-actions">
                                        {props.settledView && renderVerify(debt)}
                                        {pending && props.onSettle && (
                                            <button type="button" className="cf-icon-btn cf-icon-btn--accent" title={directionOf(debt) === 'receivable' ? 'Registrar cobro de esta deuda' : 'Registrar pago de esta deuda'} aria-label="Registrar movimiento" onClick={() => props.onSettle!(debt)}>
                                                <MoneyHand20Regular />
                                            </button>
                                        )}
                                        <button type="button" className="cf-icon-btn" title="Ver detalle" aria-label="Ver detalle" onClick={() => props.onView(debt)}>
                                            <Eye20Regular />
                                        </button>
                                        <button type="button" className="cf-icon-btn" title="Editar" aria-label="Editar" onClick={() => props.onEdit(debt)}>
                                            <Edit20Regular />
                                        </button>
                                        {pending ? (
                                            <ConfirmationModalComponent
                                                onDismiss={() => {}}
                                                onConfirm={() => props.onDelete(debt)}
                                                title="Eliminar deuda"
                                                message={`¿Confirmás que querés eliminar la deuda de ${name} por ${fmt(debt.originalAmount, currency)}?`}
                                                buttonApparence="subtle"
                                                buttonShape="square"
                                                buttonTitle=""
                                                buttonIcon={<Delete20Regular />}
                                                isProcessing={props.isDeleting}
                                            />
                                        ) : (
                                            <button type="button" className="cf-icon-btn" disabled title="No se puede eliminar una deuda saldada" aria-label="Eliminar">
                                                <Delete20Regular />
                                            </button>
                                        )}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={4}>Total ({allDebts.length})</td>
                        <td className="cf-r cf-num">{totalsByCurrency(allDebts, (debt) => debt.originalAmount ?? 0, currencyOf)}</td>
                        <td className="cf-r cf-num">{totalsByCurrency(allDebts, (debt) => debt.paidAmount ?? 0, currencyOf)}</td>
                        <td className="cf-r cf-num">{totalsByCurrency(allDebts, (debt) => (isPending(debt) ? debt.pendingAmount ?? 0 : 0), currencyOf)}</td>
                        <td />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// ── Pagos y cobros ────────────────────────────────────────────────────────

export interface PaymentsTableProps {
    /** Las filas de la página actual. */
    payments: Payment[];
    /** Todas las filas del filtro, para que el pie sume el total y no la página. */
    allPayments?: Payment[];
    /** 'received': cobros que recibió el centro; 'done': pagos que hizo. */
    kind: 'received' | 'done';
    isLoading: boolean;
    nameOf: (identifier: string) => string;
    onViewImputations: (payment: Payment) => void;
    onImpute: (payment: Payment) => void;
    onEdit: (payment: Payment) => void;
    onDelete: (payment: Payment) => void;
    isDeleting: boolean;
    /**
     * Pagos hechos a nombre de otro: acreedores de las deudas que canceló (vacío si el receptor es el acreedor
     * o no se sabe). Con valor, la fila muestra "Pagado a otro receptor" y "Deuda de …".
     */
    otherReceiverOf?: (payment: Payment) => string[];
}

export const PaymentsTable = (props: PaymentsTableProps) => {
    const counterpartOf = (payment: Payment) => (props.kind === 'received' ? payment.payer : payment.receiver);
    const allPayments = props.allPayments ?? props.payments;

    if (props.isLoading && props.payments.length === 0) {
        return <div className="cf-loading"><Spinner size="extra-tiny" /> Cargando movimientos…</div>;
    }

    if (props.payments.length === 0) {
        return <EmptyState
            title={props.kind === 'received' ? 'Todavía no hay cobros registrados' : 'Todavía no hay pagos registrados'}
            hint="Registralos con el botón Agregar y después imputalos a las deudas que cancelan." />;
    }

    const imputationTag = (payment: Payment) => {
        const pending = payment.pendingIndictmentAmount ?? 0;
        const imputed = payment.imputedAmount ?? 0;
        if (pending <= 0) return <span className="cf-tag cf-tag--imputed">Imputado</span>;
        if (imputed > 0) return <span className="cf-tag cf-tag--partial">Parcial</span>;
        return <span className="cf-tag cf-tag--unimputed">Sin imputar</span>;
    };

    return (
        <div className="cf-table-wrap">
            <table className="cf-table">
                <thead>
                    <tr>
                        <th>Contraparte</th>
                        <th>Fecha</th>
                        <th>Imputación</th>
                        <th className="cf-r">Monto</th>
                        <th className="cf-r">Imputado</th>
                        <th className="cf-r">Sin imputar</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {props.payments.map((payment) => {
                        const currency = currencyOf(payment);
                        const pending = payment.pendingIndictmentAmount ?? 0;
                        const name = props.nameOf(counterpartOf(payment));
                        const debtOf = props.kind === 'done' ? (props.otherReceiverOf?.(payment) ?? []) : [];
                        return (
                            <tr key={payment.id} className={pending > 0 ? 'is-attention' : undefined} data-testid={`cf-payment-row-${payment.id}`}>
                                <td>
                                    <PartyCell name={name} comment={payment.comments || undefined} />
                                    {debtOf.length > 0 && (
                                        <span className="cf-other-receiver" data-testid={`cf-other-receiver-${payment.id}`}>
                                            <StatusPill tone="warning" symbol="!">Pagado a otro receptor</StatusPill>
                                            <span className="cf-muted"> Deuda de {debtOf.map((id) => props.nameOf(id)).join(', ')}</span>
                                        </span>
                                    )}
                                </td>
                                <td className="cf-num cf-muted">{fmtDate(payment.paymentDate)}</td>
                                <td>{imputationTag(payment)}</td>
                                <td className="cf-r cf-num cf-amount">{fmt(payment.amount, currency)}</td>
                                <td className="cf-r cf-num cf-amount cf-amount--muted">{fmt(payment.imputedAmount, currency)}</td>
                                <td className={`cf-r cf-num cf-amount${pending > 0 ? ' cf-amount--pending' : ' cf-amount--muted'}`}>
                                    {pending > 0 ? fmt(pending, currency) : '—'}
                                </td>
                                <td className="cf-r">
                                    <span className="cf-actions">
                                        <button type="button" className="cf-icon-btn" title="Ver imputaciones" aria-label="Ver imputaciones" onClick={() => props.onViewImputations(payment)}>
                                            <DocumentBulletList20Regular />
                                        </button>
                                        {pending > 0 && (
                                            <button type="button" className="cf-icon-btn cf-icon-btn--accent" title={`Imputar saldo pendiente: ${fmt(pending, currency)}`} aria-label="Imputar saldo pendiente" onClick={() => props.onImpute(payment)}>
                                                <ArrowImport20Regular />
                                            </button>
                                        )}
                                        <button type="button" className="cf-icon-btn" title="Editar" aria-label="Editar" onClick={() => props.onEdit(payment)}>
                                            <Edit20Regular />
                                        </button>
                                        <ConfirmationModalComponent
                                            onDismiss={() => {}}
                                            onConfirm={() => props.onDelete(payment)}
                                            title={props.kind === 'received' ? 'Eliminar cobro' : 'Eliminar pago'}
                                            message={`¿Confirmás que querés eliminar el movimiento de ${name} por ${fmt(payment.amount, currency)}?`}
                                            buttonApparence="subtle"
                                            buttonShape="square"
                                            buttonTitle=""
                                            buttonIcon={<Delete20Regular />}
                                            isProcessing={props.isDeleting}
                                        />
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3}>Total ({allPayments.length})</td>
                        <td className="cf-r cf-num">{totalsByCurrency(allPayments, (payment) => payment.amount ?? 0, currencyOf)}</td>
                        <td className="cf-r cf-num">{totalsByCurrency(allPayments, (payment) => payment.imputedAmount ?? 0, currencyOf)}</td>
                        <td className="cf-r cf-num">{totalsByCurrency(allPayments, (payment) => payment.pendingIndictmentAmount ?? 0, currencyOf)}</td>
                        <td />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

